import { noopLogger } from '@/core/logger';
/**
 * What survives a page change when `features.persistence` is on.
 *
 *   localStorage    <ns>:conversation   `<savedAt>:<chatId>` — which chat to resume
 *   localStorage    <ns>:transcript     the rendered messages, for an instant repaint
 *   sessionStorage  <ns>:ui             open/closed and the unsent draft, per tab
 *
 * `<ns>` is `agentic-chat`, or the widget's own `storageNamespace`.
 *
 * The server stays the source of truth: the stored id is re-validated on every
 * resume, and the local transcript is only used while it agrees with the
 * server's (see `mergeTranscript`). The local copy exists because the server
 * transcript is plain text — suggestion chips, sources, tables and feedback
 * would otherwise vanish on every page change.
 *
 * Every access is best-effort: private mode, a full quota or a sandboxed
 * iframe degrade to "nothing stored", never to an error.
 */
/**
 * Every key is under a namespace: `agentic-chat` for the ordinary chat, its
 * own for another widget on the page — the private data chat keeps
 * `agentic-chat:data`, so the two never resume each other's conversation.
 */
export const DEFAULT_STORAGE_NAMESPACE = 'agentic-chat';
const conversationKey = (ns) => `${ns}:conversation`;
const transcriptKey = (ns) => `${ns}:transcript`;
const uiKey = (ns) => `${ns}:ui`;
/** Resume a conversation for a day; after that, start clean. */
export const DEFAULT_PERSISTENCE_TTL_MS = 24 * 60 * 60 * 1000;
/** Enough to repaint a long chat; older turns still come back from the server. */
const MAX_STORED_MESSAGES = 100;
/* --------------------------------------------------------- conversation id -- */
/**
 * Stored as `<timestamp>:<id>` so a conversation abandoned days ago isn't
 * silently resumed. Plain ids written by older builds are still readable.
 */
export function readStoredId(ttlMs = DEFAULT_PERSISTENCE_TTL_MS, logger = noopLogger, ns = DEFAULT_STORAGE_NAMESPACE) {
    try {
        const raw = localStorage.getItem(conversationKey(ns));
        if (!raw)
            return undefined;
        const separator = raw.indexOf(':');
        if (separator === -1)
            return raw; // legacy value, no timestamp
        const savedAt = Number(raw.slice(0, separator));
        const id = raw.slice(separator + 1);
        if (!id)
            return undefined;
        if (Number.isFinite(savedAt) && ttlMs > 0 && Date.now() - savedAt > ttlMs) {
            logger.scope('resume').debug('stored conversation expired', {
                chatId: id,
                ageMs: Date.now() - savedAt,
                ttlMs,
            });
            localStorage.removeItem(conversationKey(ns));
            localStorage.removeItem(transcriptKey(ns));
            return undefined;
        }
        return id;
    }
    catch (err) {
        logger.scope('resume').warn('localStorage unavailable', {
            error: err instanceof Error ? err.message : String(err),
        });
        return undefined;
    }
}
export function storeId(id, ns = DEFAULT_STORAGE_NAMESPACE) {
    try {
        localStorage.setItem(conversationKey(ns), `${Date.now()}:${id}`);
    }
    catch {
        /* private mode — persistence is best-effort */
    }
}
/** Returns whether anything was actually there to remove. */
export function clearStoredId(ns = DEFAULT_STORAGE_NAMESPACE) {
    try {
        const had = localStorage.getItem(conversationKey(ns)) !== null;
        localStorage.removeItem(conversationKey(ns));
        return had;
    }
    catch {
        return false;
    }
}
/* -------------------------------------------------------------- transcript -- */
/** The stored transcript, only if it belongs to `chatId`. */
export function readTranscript(chatId, ns = DEFAULT_STORAGE_NAMESPACE) {
    try {
        const raw = localStorage.getItem(transcriptKey(ns));
        if (!raw)
            return [];
        const stored = JSON.parse(raw);
        if (stored.chatId !== chatId || !Array.isArray(stored.messages))
            return [];
        return stored.messages;
    }
    catch {
        return [];
    }
}
export function writeTranscript(chatId, messages, ns = DEFAULT_STORAGE_NAMESPACE) {
    const keep = messages.filter(isPersistable).slice(-MAX_STORED_MESSAGES).map(serialisable);
    try {
        if (!keep.length) {
            localStorage.removeItem(transcriptKey(ns));
            return;
        }
        const value = { chatId, savedAt: Date.now(), messages: keep };
        localStorage.setItem(transcriptKey(ns), JSON.stringify(value));
    }
    catch {
        // Quota exceeded: a stale copy is worse than none — the server still has it.
        try {
            localStorage.removeItem(transcriptKey(ns));
        }
        catch {
            /* storage unavailable */
        }
    }
}
export function clearTranscript(ns = DEFAULT_STORAGE_NAMESPACE) {
    try {
        localStorage.removeItem(transcriptKey(ns));
    }
    catch {
        /* storage unavailable */
    }
}
/**
 * Only settled visitor/agent turns. The local greeting is re-rendered from
 * config, and in-flight or failed turns have no server counterpart to line up
 * with, so storing them would make the copy disagree with the server.
 */
function isPersistable(message) {
    return ((message.role === 'user' || message.role === 'assistant') &&
        message.status === 'complete' &&
        message.metadata?.local !== true);
}
/** `error.cause` can hold an Error or a Response, neither of which survives JSON. */
function serialisable(message) {
    if (!message.error)
        return message;
    const { cause: _cause, ...error } = message.error;
    return { ...message, error };
}
/**
 * Prefer the local copy, which keeps what the server transcript drops
 * (suggestions, sources, tables, feedback), but only while it agrees with the
 * server: the same roles in the same order, and identical visitor turns.
 * Anything the server has beyond it — a reply that landed after the page
 * changed, or a turn sent from another tab — is appended. Any disagreement
 * means the local copy is stale, and the server's transcript wins outright.
 */
export function mergeTranscript(local, server) {
    if (!server.length)
        return local;
    if (!local.length || local.length > server.length)
        return server;
    const agrees = local.every((message, index) => {
        const remote = server[index];
        return (remote.role === message.role &&
            (message.role !== 'user' || remote.content.trim() === message.content.trim()));
    });
    return agrees ? [...local, ...server.slice(local.length)] : server;
}
/* ---------------------------------------------------------------- UI state -- */
/** Per tab: a new tab shouldn't pop the chat open because another one had it. */
export function readUiState(ns = DEFAULT_STORAGE_NAMESPACE) {
    try {
        const raw = sessionStorage.getItem(uiKey(ns));
        if (!raw)
            return undefined;
        const stored = JSON.parse(raw);
        return { isOpen: stored.isOpen === true, draft: typeof stored.draft === 'string' ? stored.draft : '' };
    }
    catch {
        return undefined;
    }
}
export function writeUiState(state, ns = DEFAULT_STORAGE_NAMESPACE) {
    try {
        sessionStorage.setItem(uiKey(ns), JSON.stringify(state));
    }
    catch {
        /* storage unavailable */
    }
}
export function clearUiState(ns = DEFAULT_STORAGE_NAMESPACE) {
    try {
        sessionStorage.removeItem(uiKey(ns));
    }
    catch {
        /* storage unavailable */
    }
}
